import type { SchemaType } from '@based/schema'
import { SchemaToType } from './utils'

export const SERVICE_PROVIDER_TYPES = {
  financing: 'financing',
  processCoordinator: 'processCoordinator',
  architect: 'architect',
  consultancy: 'consultancy',
  construction: 'construction',
  development: 'development',
  network: 'network',
  supportGroup: 'supportGroup',
} as const
export type ServiceProviderTypeEnum = keyof typeof SERVICE_PROVIDER_TYPES

export const serviceProvider = {
  uuid: { type: 'alias', format: 'UUID' },
  name: 'string',
  slug: { type: 'alias', format: 'slug' },
  intro: 'text',
  description: 'text',
  type: { items: { ref: 'serviceProviderType', prop: 'serviceProviders' } },
  avatar: { ref: 'file', prop: 'serviceProviderAvatar' },
  banner: { ref: 'file', prop: 'serviceProviderBanner' },
  events: { items: { ref: 'event', prop: 'serviceProviders' } },
  initiatives: { items: { ref: 'initiative', prop: 'serviceProviders' } },
  specializations: {
    items: { ref: 'serviceProviderSpecialization', prop: 'serviceProviders' },
  },
  activeRegions: { items: { ref: 'region', prop: 'activeServiceProviders' } },
  location: {
    props: {
      geo: {
        props: {
          lat: 'number',
          long: 'number',
        },
      },
      address: {
        props: {
          country: { ref: 'country', prop: 'serviceProviders' },
          region: { ref: 'region', prop: 'serviceProviders' },
          municipality: { ref: 'municipality', prop: 'serviceProviders' },
          city: 'string',
          street: 'string',
          postalCode: 'string',
        },
      },
    },
  },
  serviceProvidersConcepts: {
    items: { ref: 'serviceProviderConcept', prop: 'serviceProviders' },
  },
  contentBlocks: {
    items: {
      ref: 'pageContentBlock',
      prop: 'serviceProvider',
      $position: [
        'overviewTab',
        'developmentAreaTab',
        'conceptsTab',
        'eventsTab',
        'initiativesTab',
      ],
    },
  },
  contact: {
    props: {
      name: 'string',
      email: 'string',
      phone: 'string',
      website: 'string',
    },
  },
  media: { items: { ref: 'file', prop: 'serviceProviderMedia' } },
  // createdBy: {
  //   items: {
  //     prop: 'createdServiceProviders',
  //     ref: 'user',
  //   },
  // },
  businessProfiles: {
    items: { ref: 'businessProfile', prop: 'serviceProviders' },
  },
  status: ['active', 'inactive'],
  featureToggles: { items: { ref: 'featureToggle', prop: 'serviceProviders' } },
  createdAt: {
    type: 'timestamp',
    on: 'create',
  },
  updatedAt: {
    type: 'timestamp',
    on: 'update',
  },
  spotlights: { items: { ref: 'spotlight', prop: 'serviceProvider' } },
  followers: { items: { ref: 'user', prop: 'followedServiceProviders' } },
  thread: { ref: 'thread', prop: 'serviceProvider' },
  relatedTopicCommunities: {
    items: { ref: 'topicCommunity', prop: 'relatedServiceProviders' },
  },
  knowledgeHubModules: {
    items: { ref: 'knowledgeHubModule', prop: 'serviceProviders' },
  },
  housingForms: {
    items: { ref: 'housingForm', prop: 'serviceProviders' },
  },
  stories: {
    items: { ref: 'stories', prop: 'serviceProviders' },
  },
  news: {
    items: { ref: 'news', prop: 'serviceProviders' },
  },
  blogs: {
    items: { ref: 'blog', prop: 'serviceProviders' },
  },
} satisfies SchemaType

export type ServiceProvider = SchemaToType<typeof serviceProvider> & {
  id: number
}

export const serviceProviderType = {
  uuid: { type: 'alias', format: 'UUID' },
  name: { type: 'text' },
  description: 'text',
  type: Object.values(SERVICE_PROVIDER_TYPES),
  serviceProviders: { items: { ref: 'serviceProvider', prop: 'type' } },
  createdAt: {
    type: 'timestamp',
    on: 'create',
  },
  updatedAt: {
    type: 'timestamp',
    on: 'update',
  },
} satisfies SchemaType

export type ServiceProviderType = SchemaToType<typeof serviceProviderType> & {
  id: number
}

export const serviceProviderSpecialization = {
  uuid: { type: 'alias', format: 'UUID' },
  name: 'string',
  description: 'text',
  serviceProviders: {
    items: { ref: 'serviceProvider', prop: 'specializations' },
  },
  createdAt: {
    type: 'timestamp',
    on: 'create',
  },
  updatedAt: {
    type: 'timestamp',
    on: 'update',
  },
} satisfies SchemaType

export type ServiceProviderSpecialization = SchemaToType<
  typeof serviceProviderSpecialization
> & {
  id: number
}
